import { useSubscriptionsStore } from "@/stores/subscriptions";
import {
  premiumLCNs,
  allstateBasicEligibleLCNs,
  nonAllstateWebrootLCNs,
  allstateLCNs,
  allstatePremiumFamilyLCNs,
  webrootCDLPcodes,
  webrootCDLP,
  bestBuyAllStateLCNs,
} from "@/common/webrootProductName";
import { useAllstateStore } from "@/stores/allstate";
import {
  cancelled,
  SUBSCRIPTION_BESTBUY_RENEWAL_CONVERSION_DAY,
  SUBSCRIPTION_LAST_ALLSTATE_DAY,
  SUBSCRIPTION_MIN_ACTIVE_DAY,
  SUBSCRIPTION_MIN_EXPIRED_DAY,
  SUBSCRIPTION_MIN_WARNING_DAY,
  URLS,
} from "@/define";
import { useNotificationsStore } from "@/stores/notifications";
import { unifiedApi } from "@/common";
import { getUrl } from "@/common/getUrl";
import { IAllstateResponse, ISubscription } from "@/common/api/unifiedPortal/interfaces";
import { CancellationReasons, KeyCodeSeller, KeycodeStatus } from "@/globalEnums";
import { pageStates } from "./AllstateEnums";
import { t } from "@/i18n";
import { hasActiveVPNSubscription } from "@/common/subscriptionHelper";

async function getSubscriptions() {
  const subscriptionsStore = useSubscriptionsStore();
  await subscriptionsStore.populateSubscriptions();
  const subscriptions = subscriptionsStore.subscriptions;
  return subscriptions;
}

async function getAllstateSubscriptions() {
  const allstateStore = useAllstateStore();
  const subscriptions = (await allstateStore.getAllstateSubscriptions()).allstateSubscriptions;
  return subscriptions;
}

export function calculateRemainingDays(remainingDays, keycodeSellerType) {
  const allstateStore = useAllstateStore();
  return keycodeSellerType === KeyCodeSeller.BestBuy && allstateStore.allStateInfo.hasAccountSetup
    ? remainingDays + SUBSCRIPTION_BESTBUY_RENEWAL_CONVERSION_DAY * -1
    : remainingDays;
}

// Returns if the user has non-Allstate Webroot subscriptions
export async function hasWebrootSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        nonAllstateWebrootLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
    );
  return response;
}

export async function hasActivePremiumSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        premiumLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > 0 &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Active
    );
  return response;
}

export async function hasActivePremiumFamilySubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        allstatePremiumFamilyLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > 0 &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Active
    );
  return response;
}

export async function hasActiveCDLPSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        webrootCDLPcodes.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Active
    );
  return response;
}

export async function hasInactivePremiumSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        premiumLCNs.includes(l.licenseCategoryName ?? "") &&
        l.webrootStatusDescription.toLowerCase() === KeycodeStatus.Inactive
    );
  return response;
}

export async function hasExpiredPremiumSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        premiumLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) < 0 &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) >= SUBSCRIPTION_LAST_ALLSTATE_DAY
    );
  return response;
}

export async function hasExpiredPremiumFamilySubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        allstatePremiumFamilyLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) < 0 &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) >= SUBSCRIPTION_LAST_ALLSTATE_DAY
    );
  return response;
}

export async function hasAddOnSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        allstateBasicEligibleLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
    );
  return response;
}

export async function hasAllstateSubscriptions() {
  const subscriptions = await getSubscriptions();
  const response =
    subscriptions &&
    subscriptions.some(
      l =>
        allstateLCNs.includes(l.licenseCategoryName ?? "") &&
        calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_LAST_ALLSTATE_DAY
    );
  return response;
}

export async function hasAllstateAccountSetup() {
  const allstateSubscriptions = await getAllstateSubscriptions();
  const response = allstateSubscriptions && allstateSubscriptions.some(a => a.canceledOnDate === null);
  return response;
}

export async function hasAllstateUpgradeNotification() {
  const allstateSubscriptions = await getAllstateSubscriptions();
  const response = allstateSubscriptions && allstateSubscriptions.some(a => a.canceledOnDate === null);
  return response;
}

export function dismissAllstateUpgradeNotification() {
  const notificationsStore = useNotificationsStore();
  notificationsStore.clearNotifications();
  unifiedApi.updateAllstateSubscriptions({ showUpgradedBanner: false });
  const allstateStore = useAllstateStore();
  const allstateSubs = allstateStore.allstateSubscriptions;
  allstateSubs.forEach(l => {
    l.showUpgradedBanner = false;
  });
  allstateStore.allStateInfo.allstateSubscriptions = allstateSubs;
}

// allstate upgrade banner displays if More than 90 days from sub expiring and api returns showUpgradedBanner and flag is set to show allstate family
export async function showAllstateBanner() {
  const notificationsStore = useNotificationsStore();
  const subscriptionsStore = useSubscriptionsStore();
  const allstateStore = useAllstateStore();

  if (subscriptionsStore.subscriptions.length === 0) {
    await subscriptionsStore.populateSubscriptions();
  }
  const allstateSubscription = subscriptionsStore.subscriptions.find(l =>
    allstateLCNs.includes(l.licenseCategoryName ?? "")
  );
  if (
    allstateStore.allstateSubscriptions.some(l => l.showUpgradedBanner) &&
    allstateSubscription?.remainingDays &&
    allstateSubscription?.remainingDays > SUBSCRIPTION_MIN_ACTIVE_DAY &&
    allstateSubscription.zuoraSubscriptionStatus.toLowerCase() !== cancelled
  ) {
    const lcn = allstateSubscription.licenseCategoryName;
    notificationsStore.clearNotifications();
    notificationsStore.addNotification({ type: "AllstateUpgradeInfo", params: { licenseCategoryName: lcn } });
  }
}

export async function getUpgradeCartUrl(keycode: string) {
  const url = (await unifiedApi.getWebrootRenewUrl(keycode)).data.redirectUrl;
  if (url) {
    const queryString = url.split("?")[1];
    // This URL will be only used here, so no need to add to env files.
    return `${getUrl("ALLSTATE_UPGRADE_CART")}?${queryString}`;
  }
}

export function getAllstateManageUrl() {
  return `${URLS.UP_ENDPOINT.protocol}://${URLS.UP_ENDPOINT.hostname}/api/cp/auth/allstate`;
}

export function getActiveAllState(allstateSubscriptions: IAllstateResponse[], subscriptions: ISubscription[]) {
  return allstateSubscriptions.length > 0 &&
    subscriptions.some(
      l =>
        l.keyCode === allstateSubscriptions[0].keyCode &&
        l.keyCodeSellerType === KeyCodeSeller.Webroot &&
        !allstateSubscriptions[0].canceledOnDate
    )
    ? subscriptions.find(
        l =>
          l.keyCode === allstateSubscriptions[0].keyCode &&
          l.keyCodeSellerType === KeyCodeSeller.Webroot &&
          !allstateSubscriptions[0].canceledOnDate
      )
    : subscriptions.some(
          l =>
            allstateLCNs.includes(l.licenseCategoryName ?? "") &&
            calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
        )
      ? subscriptions.find(
          l =>
            allstateLCNs.includes(l.licenseCategoryName ?? "") &&
            calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
        )
      : subscriptions.find(
          l =>
            allstateLCNs.includes(l.licenseCategoryName ?? "") &&
            calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) <= SUBSCRIPTION_MIN_EXPIRED_DAY
        );
}

export function setPageState() {
  const allstateStore = useAllstateStore();

  if (allstateStore.hasEnrolledAllstateSubscription) {
    allstateStore.pageState = pageStates.alreadySignedUp;
  } else if (hasActiveAllStateSubscription()) {
    allstateStore.pageState = pageStates.canSignUp;
  } else if (canUpgradeToAllstate()) {
    allstateStore.pageState = pageStates.upgrade;
  } else {
    allstateStore.pageState = pageStates.notPurchased;
  }
}

// Possible pageStates:
// alreadySignedUp - User has a Allstate subscription already
// canSignUp - User has a Webroot Premium (WSAP), or a add-on (WSID) Webroot License
// upgrade - User has a Webroot license bought from ECom (keyCodeSellerType === Webroot), but not one above
// notPurchased - User has other subscriptions such as Safe or even Webroot from Carbonite. It need to buy a new subscription from ECom.
export async function loadPageState() {
  const notificationsStore = useNotificationsStore();
  const allstateStore = useAllstateStore();
  const subscriptionsStore = useSubscriptionsStore();

  setPageState();

  // get activeSubscription and calculate the subscriptionName, keycode and expiration state
  allstateStore.activeSubscription = getActiveAllState(
    allstateStore.allstateSubscriptions,
    subscriptionsStore.subscriptions
  );

  if (!allstateStore.activeSubscription) {
    return;
  }

  allstateStore.keycode = allstateStore.activeSubscription.keyCode ?? "";
  allstateStore.subscriptionName = t(`Brand.${allstateStore.activeSubscription.licenseCategoryName}`);
  allstateStore.subscriptionSku = allstateStore.activeSubscription.licenseCategoryName ?? "";
  allstateStore.keycode = allstateStore.activeSubscription.keyCode ?? "";
  if (allstateStore.activeSubscription.webrootStatusDescription.toLowerCase() === KeycodeStatus.Inactive) {
    notificationsStore.addNotification({ type: "InactiveKeycodeAllstateError" });
    allstateStore.isInactive = true;
    return;
  }
  const webrootUserInfo = (await unifiedApi.getWebrootPIIInformation(allstateStore.activeSubscription.keyCode ?? ""))
    ?.data;
  const canAutoEnroll = autoEnrollEnabled(webrootUserInfo);
  allstateStore.isExpired =
    allstateStore.activeSubscription.hasOutstandingInvoices ||
    (calculateRemainingDays(
      allstateStore.activeSubscription.remainingDays,
      allstateStore.activeSubscription.keyCodeSellerType
    ) <= SUBSCRIPTION_MIN_EXPIRED_DAY &&
      calculateRemainingDays(
        allstateStore.activeSubscription.remainingDays,
        allstateStore.activeSubscription.keyCodeSellerType
      ) >= SUBSCRIPTION_LAST_ALLSTATE_DAY) ||
    (allstateStore.activeSubscription.zuoraSubscriptionStatus?.toLowerCase() === cancelled &&
      allstateStore.activeSubscription.cancellationReason !== CancellationReasons.FlatRateUpgradeRefund);

  if (
    (allstateStore.activeSubscription.hasOutstandingInvoices || allstateStore.isExpired) &&
    webrootCDLP.some(s => s !== allstateStore.activeSubscription?.sku) &&
    !hasActiveVPNSubscription(subscriptionsStore.subscriptions)
  ) {
    allstateStore.pageState = pageStates.alreadySignedUp;
    notificationsStore.addNotification({
      type: canAutoEnroll ? "AllstateExpiredError" : "AllstateExpiredErrorReEnroll",
      params: {
        subscription: allstateStore.subscriptionName,
        keycode: allstateStore.activeSubscription.keyCode,
      },
    });

    return;
  }

  checkExpirationState(canAutoEnroll);
}

export function checkExpirationState(canAutoEnroll: boolean = false) {
  const notificationsStore = useNotificationsStore();
  const allstateStore = useAllstateStore();
  const subscriptionsStore = useSubscriptionsStore();

  if (!allstateStore.activeSubscription) {
    return;
  }

  allstateStore.isExpiring =
    allstateStore.activeSubscription.remainingDays > SUBSCRIPTION_MIN_EXPIRED_DAY &&
    allstateStore.activeSubscription.remainingDays <= SUBSCRIPTION_MIN_WARNING_DAY &&
    !allstateStore.activeSubscription.isAutoRenewEnabled;

  //Get all the non BestBuy AllState subs
  const allStateSubsNonBestBuy = allstateStore.allstateSubscriptions.filter(l => {
    const sub = subscriptionsStore.subscriptions.find(n => n.keyCode === l.keyCode);
    if (!sub) {
      return false;
    }

    return !bestBuyAllStateLCNs.some(k => k === sub.licenseCategoryName);
  });

  if (allstateStore.isExpiring && allStateSubsNonBestBuy.length > 0) {
    notificationsStore.addNotification({
      type: canAutoEnroll ? "AllstateExpiringWarning" : "AllstateExpiringWarningReEnroll",
      params: {
        subscription: allstateStore.subscriptionName,
        keycode: allstateStore.keycode,
      },
    });
  }
}

export async function completeSignUp(response: IAllstateResponse) {
  const allstateStore = useAllstateStore();
  const notificationsStore = useNotificationsStore();

  notificationsStore.clearNotifications();
  allstateStore.allStateInfo.allstateSubscriptions = [];
  allstateStore.allStateInfo.allstateSubscriptions.push(response);
  notificationsStore.addNotification({ type: "AllstateSignUpComplete" });
  allstateStore.justSet = true;
  await loadPageState();
}

export function autoEnrollEnabled(webrootUserInfo) {
  if (!webrootUserInfo) {
    return false;
  }

  return (
    webrootUserInfo.country === "US" &&
    webrootUserInfo.firstName?.length > 0 &&
    webrootUserInfo.lastName?.length > 0 &&
    webrootUserInfo.address1?.length > 0 &&
    webrootUserInfo.state?.length > 0 &&
    webrootUserInfo.city?.length > 0 &&
    webrootUserInfo.postalCode?.length > 0
  );
}

function hasActiveAllStateSubscription() {
  const subscriptionsStore = useSubscriptionsStore();
  return (
    subscriptionsStore.subscriptions &&
    subscriptionsStore.subscriptions.some(
      l =>
        allstateLCNs.includes(l.licenseCategoryName ?? "") &&
        (calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY ||
          l.webrootStatusDescription?.toLowerCase() === KeycodeStatus.Inactive)
    )
  );
}

function canUpgradeToAllstate() {
  const subscriptionsStore = useSubscriptionsStore();
  return subscriptionsStore.subscriptions.some(
    l =>
      (l.keyCodeSellerType === KeyCodeSeller.Webroot || l.keyCodeSellerType === KeyCodeSeller.BestBuy) &&
      calculateRemainingDays(l.remainingDays, l.keyCodeSellerType) > SUBSCRIPTION_MIN_EXPIRED_DAY
  );
}
